/* =========================================================
   VELMORA — Product page interactions
   Variant selection (URL-synced, no reload), gallery swipe,
   zoom lightbox, inventory messaging. All data comes from the
   real Shopify variant payload rendered by Liquid.
   ========================================================= */
(function () {
  "use strict";

  function money(cents) {
    if (window.Velmora && window.Velmora.formatMoney) {
      return window.Velmora.formatMoney(cents);
    }
    return (cents / 100).toFixed(2);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      var ctx = this;
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /* ---------------- Gallery ---------------- */
  function initGallery(root) {
    var track = root.querySelector("[data-gallery-track]");
    if (!track) return null;
    var slides = Array.prototype.slice.call(track.querySelectorAll("[data-gallery-slide]"));
    var dots = Array.prototype.slice.call(root.querySelectorAll("[data-gallery-dot]"));

    function setActive(index) {
      dots.forEach(function (d, i) {
        d.classList.toggle("is-active", i === index);
      });
    }

    if (dots.length) {
      track.addEventListener(
        "scroll",
        debounce(function () {
          var slideWidth = slides[0] ? slides[0].offsetWidth : 1;
          if (!slideWidth) return;
          setActive(Math.round(track.scrollLeft / slideWidth));
        }, 80),
        { passive: true }
      );

      dots.forEach(function (dot, i) {
        dot.addEventListener("click", function () {
          goToIndex(i);
        });
      });
    }

    function goToIndex(i) {
      var slide = slides[i];
      if (!slide) return;
      if (window.innerWidth < 990) {
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: "smooth" });
      } else {
        slide.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setActive(i);
    }

    function goToMedia(mediaId) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute("data-gallery-media") === String(mediaId)) {
          goToIndex(i);
          return;
        }
      }
    }

    return { goToIndex: goToIndex, goToMedia: goToMedia };
  }

  /* ---------------- Zoom lightbox ---------------- */
  function initZoom(root) {
    var lightbox = document.querySelector("[data-zoom-lightbox]");
    if (!lightbox) return;
    var img = lightbox.querySelector("img");

    root.addEventListener("click", function (e) {
      var trigger = e.target.closest("[data-zoom-trigger]");
      if (!trigger) return;
      var src = trigger.getAttribute("data-zoom-src");
      if (img && src) {
        img.src = src;
        img.alt = "";
      }
      lightbox.classList.add("is-open");
      lightbox.setAttribute("aria-hidden", "false");
      document.body.classList.add("no-scroll");
    });

    lightbox.addEventListener("click", function (e) {
      if (e.target === lightbox || e.target.closest("[data-drawer-close]")) {
        lightbox.classList.remove("is-open");
        lightbox.setAttribute("aria-hidden", "true");
        document.body.classList.remove("no-scroll");
      }
    });
  }

  /* ---------------- Custom-name engraving ----------------
     Required on Custom Name products only (see main-product.liquid for
     detection). Validated on the form element itself so this listener
     runs before cart.js's document-level submit handler ever sees the
     event — at-target listeners always fire ahead of an ancestor's bubble
     listener, regardless of script load order — and a failed check calls
     stopPropagation so the AJAX add never happens. `required` stays on the
     input as a no-JS fallback; the `invalid` listener only swaps the
     browser's native bubble for the theme's own inline message. */
  function initEngrave(root) {
    var form = root.querySelector("form[data-cart-form]");
    var field = root.querySelector("[data-engrave-field]");
    if (!form || !field) return null;

    var input = field.querySelector("[data-engrave-input]");
    var errorEl = field.querySelector("[data-engrave-error]");
    var countEl = field.querySelector("[data-engrave-count]");
    if (!input) return null;
    var maxLen = parseInt(input.getAttribute("maxlength"), 10) || 20;

    function updateCount() {
      if (!countEl) return;
      countEl.textContent = input.value.length + " / " + maxLen;
    }

    function setError(message) {
      if (errorEl) {
        errorEl.textContent = message || "";
        errorEl.hidden = !message;
      }
      input.classList.toggle("has-error", !!message);
    }

    function isValid() {
      return input.value.trim().length > 0;
    }

    input.addEventListener("input", function () {
      updateCount();
      if (isValid()) setError("");
    });

    input.addEventListener("blur", function () {
      input.value = input.value.trim();
    });

    // Suppress the native validation bubble in favour of the styled message;
    // the browser still blocks the submit event on its own for a truly
    // empty value, this only changes what the visitor sees.
    input.addEventListener("invalid", function (e) {
      e.preventDefault();
      setError(input.getAttribute("data-error-required"));
    });

    form.addEventListener("submit", function (e) {
      input.value = input.value.trim();
      if (!isValid()) {
        e.preventDefault();
        e.stopPropagation();
        setError(input.getAttribute("data-error-required"));
        input.focus();
      }
    });

    updateCount();
    return { isValid: isValid, focusError: function () { setError(input.getAttribute("data-error-required")); input.focus(); } };
  }

  /* ---------------- Variant picker ---------------- */
  function initVariantPicker(root, gallery, engrave) {
    var form = root.querySelector("form[data-cart-form]");
    if (!form) return;
    var dataEl = root.querySelector("[data-variant-json]");
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!variants || !variants.length) return;

    var priceEl = root.querySelector("[data-product-price]");
    var compareEl = root.querySelector("[data-product-compare-price]");
    var addBtn = form.querySelector("[data-add-to-cart]");
    var buyNowBtn = root.querySelector("[data-buy-now]");
    var inventoryEl = root.querySelector("[data-product-inventory]");
    var idInput = form.querySelector('input[name="id"]');
    var optionGroups = Array.prototype.slice.call(root.querySelectorAll("[data-option-index]"));
    var buyBox = root.querySelector("[data-buy-box]");
    var enquiryBox = root.querySelector("[data-enquiry-box]");

    var guardZero = !(window.Velmora && window.Velmora.hideZeroPrices === false);
    var zeroLabel = (window.Velmora && window.Velmora.zeroPriceLabel) || "";

    function isZeroPriced(variant) {
      return guardZero && variant && variant.price === 0;
    }

    function selectedOptions() {
      var opts = [];
      optionGroups.forEach(function (group) {
        var index = parseInt(group.getAttribute("data-option-index"), 10);
        var selected = group.querySelector(".is-selected");
        opts[index] = selected ? selected.getAttribute("data-value") : null;
      });
      return opts;
    }

    function matchVariant(opts) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var ok = true;
        for (var j = 0; j < opts.length; j++) {
          if (opts[j] != null && v.options[j] !== opts[j]) {
            ok = false;
            break;
          }
        }
        if (ok) return v;
      }
      return null;
    }

    function updateAvailability() {
      var current = selectedOptions();
      optionGroups.forEach(function (group) {
        var index = parseInt(group.getAttribute("data-option-index"), 10);
        group.querySelectorAll("[data-value]").forEach(function (control) {
          var test = current.slice();
          test[index] = control.getAttribute("data-value");
          var match = matchVariant(test);
          control.classList.toggle("is-unavailable", !match || !match.available);
        });
      });
    }

    function updateLabels() {
      optionGroups.forEach(function (group) {
        var label = group.querySelector("[data-option-label-value]");
        var selected = group.querySelector(".is-selected");
        if (label && selected) {
          label.textContent = selected.getAttribute("data-value");
        }
      });
    }

    function setButton(btn, enabled, labelAttr) {
      if (!btn) return;
      btn.disabled = !enabled;
      var textEl = btn.querySelector(".btn__text");
      if (textEl && labelAttr) {
        var label = btn.getAttribute(labelAttr);
        if (label) textEl.textContent = label;
      }
    }

    function render() {
      var variant = matchVariant(selectedOptions());
      updateAvailability();
      updateLabels();

      if (!variant) {
        setButton(addBtn, false, "data-label-unavailable");
        if (buyNowBtn) buyNowBtn.disabled = true;
        if (inventoryEl) inventoryEl.textContent = "";
        return;
      }

      if (idInput) idInput.value = variant.id;

      try {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      } catch (e) {
        /* older browsers — skip URL sync */
      }

      var zeroPriced = isZeroPriced(variant);

      if (priceEl) {
        priceEl.textContent = zeroPriced ? zeroLabel : money(variant.price);
        priceEl.classList.toggle("price--on-request", zeroPriced);
      }
      if (compareEl) {
        if (!zeroPriced && variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (variant.featured_media && gallery) {
        gallery.goToMedia(variant.featured_media.id);
      }

      // An unpriced variant is enquiry-only: swap the buy box for the
      // enquiry panel rather than letting it be added to the cart at 0.
      if (buyBox) buyBox.hidden = zeroPriced;
      if (enquiryBox) enquiryBox.hidden = !zeroPriced;

      var purchasable = variant.available && !zeroPriced;
      setButton(addBtn, purchasable, purchasable ? "data-label-add" : "data-label-sold-out");
      if (buyNowBtn) {
        buyNowBtn.disabled = !purchasable;
        buyNowBtn.hidden = zeroPriced;
      }

      if (zeroPriced) {
        if (inventoryEl) {
          inventoryEl.textContent = "";
          inventoryEl.className = "product-info__inventory";
        }
        return;
      }

      if (inventoryEl) {
        inventoryEl.className = "product-info__inventory";
        if (!variant.available) {
          inventoryEl.textContent = inventoryEl.getAttribute("data-text-unavailable") || "";
        } else if (
          variant.inventory_management &&
          variant.inventory_policy !== "continue" &&
          typeof variant.inventory_quantity === "number" &&
          variant.inventory_quantity > 0 &&
          variant.inventory_quantity <= 5
        ) {
          var tpl = inventoryEl.getAttribute("data-text-low") || "";
          inventoryEl.textContent = tpl.replace("[count]", variant.inventory_quantity);
          inventoryEl.classList.add("is-low");
        } else {
          inventoryEl.textContent = inventoryEl.getAttribute("data-text-in-stock") || "";
        }
      }
    }

    root.addEventListener("click", function (e) {
      var control = e.target.closest("[data-option-index] [data-value]");
      if (!control) return;
      e.preventDefault();
      var group = control.closest("[data-option-index]");
      group.querySelectorAll("[data-value]").forEach(function (c) {
        c.classList.remove("is-selected");
      });
      control.classList.add("is-selected");
      render();
    });

    if (buyNowBtn) {
      buyNowBtn.addEventListener("click", async function () {
        // "Buy it now" skips /cart/add.js entirely, so it needs the same
        // required-field check the normal Add to Cart path gets from
        // initEngrave's form-submit listener — this button never fires
        // that event.
        if (engrave && !engrave.isValid()) {
          engrave.focusError();
          return;
        }
        var variant = matchVariant(selectedOptions());
        if (!variant || !variant.available || isZeroPriced(variant)) return;
        var qtyInput = form.querySelector('input[name="quantity"]');
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        var properties = {};

form.querySelectorAll('[name^="properties["]').forEach(function (propInput) {
  if (!propInput.value) return;

  var propertyName = propInput.name
    .replace(/^properties\[/, "")
    .replace(/\]$/, "");

  properties[propertyName] = propInput.value;
});

try {
  buyNowBtn.disabled = true;

  var response = await fetch(root_url + "cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify({
      items: [
        {
          id: variant.id,
          quantity: qty,
          properties: properties
        }
      ]
    })
  });

  if (!response.ok) {
    throw new Error("Unable to add item to cart");
  }

  window.location.href = root_url + "checkout";
} catch (error) {
  console.error("Buy Now error:", error);
  buyNowBtn.disabled = false;
}
      });
    }

    render();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll("[data-product-root]").forEach(function (root) {
      if (root.dataset.velmoraInit === "true") return;
      root.dataset.velmoraInit = "true";
      var gallery = initGallery(root);
      initZoom(root);
      var engrave = initEngrave(root);
      initVariantPicker(root, gallery, engrave);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (e) {
    initAll(e.target);
  });
})();
